/**
 * Production Sets API - Bates numbering and production set management.
 */

import apiClient, { type RequestConfig } from './client';

export interface ProductionSet {
  id: string;
  matter_id: string;
  name: string;
  description: string;
  bates_start: string | null;
  bates_end: string | null;
  document_count: number;
  page_count: number;
  status: 'draft' | 'finalized' | 'produced';
  created_at: string;
  updated_at: string;
  finalized_at: string | null;
  produced_at: string | null;
  created_by: string;
}

export interface BatesConfig {
  id: string;
  matter_id: string;
  prefix: string;
  suffix: string;
  format: 'numeric' | 'prefix' | 'full';
  padding: number;
  next_number: number;
}

export interface BatesAssignment {
  id: string;
  document_id: string;
  production_set_id: string | null;
  bates_begin: string;
  bates_end: string;
  page_count: number;
  family_id: string;
  family_index: number;
  assigned_at: string;
}

export interface CreateProductionSetRequest {
  name: string;
  description?: string;
}

export interface AssignBatesRequest {
  document_ids: string[];
  production_set_id?: string;
}

export interface AssignBatesResponse {
  assignments: BatesAssignment[];
  bates_start: string;
  bates_end: string;
  assigned_count: number;
}

const ENDPOINT = '/matters';

export const productionApi = {
  /**
   * List production sets for a matter.
   */
  async listSets(
    matterId: string,
    config?: RequestConfig
  ): Promise<ProductionSet[]> {
    const { data } = await apiClient.get<ProductionSet[]>(
      `${ENDPOINT}/${matterId}/production-sets`,
      config
    );
    return data;
  },

  /**
   * Create a production set.
   */
  async createSet(
    matterId: string,
    request: CreateProductionSetRequest,
    config?: RequestConfig
  ): Promise<ProductionSet> {
    const { data } = await apiClient.post<ProductionSet>(
      `${ENDPOINT}/${matterId}/production-sets`,
      request,
      config
    );
    return data;
  },

  /**
   * Get a production set by ID.
   */
  async getSet(
    matterId: string,
    setId: string,
    config?: RequestConfig
  ): Promise<ProductionSet> {
    const { data } = await apiClient.get<ProductionSet>(
      `${ENDPOINT}/${matterId}/production-sets/${setId}`,
      config
    );
    return data;
  },

  /**
   * Finalize a production set (locks Bates assignments).
   */
  async finalizeSet(
    matterId: string,
    setId: string,
    config?: RequestConfig
  ): Promise<ProductionSet> {
    const { data } = await apiClient.post<ProductionSet>(
      `${ENDPOINT}/${matterId}/production-sets/${setId}/finalize`,
      null,
      config
    );
    return data;
  },

  /**
   * Assign Bates numbers to documents.
   */
  async assignBates(
    matterId: string,
    request: AssignBatesRequest,
    config?: RequestConfig
  ): Promise<AssignBatesResponse> {
    const { data } = await apiClient.post<AssignBatesResponse>(
      `${ENDPOINT}/${matterId}/bates/assign`,
      request,
      config
    );
    return data;
  },

  /**
   * Get Bates config for a matter.
   */
  async getBatesConfig(
    matterId: string,
    config?: RequestConfig
  ): Promise<BatesConfig> {
    const { data } = await apiClient.get<BatesConfig>(
      `${ENDPOINT}/${matterId}/bates/config`,
      config
    );
    return data;
  },

  /**
   * Update Bates config (prefix, padding, etc.).
   */
  async updateBatesConfig(
    matterId: string,
    updates: Partial<Pick<BatesConfig, 'prefix' | 'suffix' | 'format' | 'padding'>>,
    config?: RequestConfig
  ): Promise<BatesConfig> {
    const { data } = await apiClient.patch<BatesConfig>(
      `${ENDPOINT}/${matterId}/bates/config`,
      updates,
      config
    );
    return data;
  },
};

export default productionApi;
